import type { ReactNode } from 'react';
import { useState } from 'react';

import omit from 'lodash/omit';

import type { ClickStatus } from '@coursera/event-pulse-types';

import * as actions from 'bundles/authentication-common/components/actions';
import {
  ARKOSE_LOGIN_KEY,
  LOGIN_REDESIGN_RECAPTCHA_ID,
  RECAPTCHA_ACTIONS,
} from 'bundles/authentication-common/constants/botManager';
import { generalErrorMessage } from 'bundles/authentication-common/constants/errors';
import useBotManager from 'bundles/authentication-common/hooks/useBotManager';
import useTrackClickButton from 'bundles/authentication-common/hooks/useTrackClickButton';
import type { LoginValues } from 'bundles/authentication-common/types';
import type { ArkoseBotManagerContext } from 'bundles/authentication-common/types/BotManagerTypes';
import { HANDLED_ERRORS, NEEDS_2FA_ERRORS, errorMessageFactory } from 'bundles/authentication-common/utils/loginUtils';
import { type EventData, trackAuthAttempts } from 'bundles/user-account/utils/authTrackingUtils';

export type UseEmailPasswordLoginOptions = {
  arkoseBotManagerContext?: ArkoseBotManagerContext;
  eventData: EventData;
  onSuccess: (redirectTo: string) => void;
  onError?: (response?: unknown) => void;
  onInvalidCredential?: () => void;
};

export type UseEmailPasswordLogin = {
  login: (values: LoginValues) => Promise<void | false>;
  error: ReactNode;
  isRunning: boolean;
  needsTwoFactorAuth: boolean;
  setError: (error: ReactNode) => void;
  setIsRunning: (isRunning: boolean) => void;
  trackLoginClick: (status: ClickStatus) => void;
  handleError: (response: unknown, overrideMessages?: Partial<Record<string, ReactNode>>) => void;
};

type ErrorMessages = ReturnType<typeof errorMessageFactory>;

const getErrorCode = (response: unknown): string | undefined => {
  const hasResponseJSON = typeof response === 'object' && response !== null && 'responseJSON' in response;
  if (!hasResponseJSON) return undefined;

  const { responseJSON } = response;
  const hasErrorCode = typeof responseJSON === 'object' && responseJSON !== null && 'errorCode' in responseJSON;
  if (!hasErrorCode) return undefined;

  const { errorCode } = responseJSON;
  return typeof errorCode === 'string' ? errorCode : undefined;
};

type InvalidCredentialCode = 'invalidCredential' | 'invalidPassword';

const isInvalidCredentialCode = (code: string): code is InvalidCredentialCode =>
  code === 'invalidCredential' || code === 'invalidPassword';

const getHandledErrorMessage = (code: string): ReactNode => {
  const messages = errorMessageFactory();
  return isInvalidCredentialCode(code)
    ? messages[code]()
    : messages[code as Exclude<keyof ErrorMessages, InvalidCredentialCode>];
};

/**
 * Email-and-password login submission: configures the bot manager for the login form,
 * tracks click/attempt events, maps API error codes to messages and exposes the
 * shared error/running state so callers (e.g. the LTI login branch) can reuse it.
 */
const useEmailPasswordLogin = ({
  arkoseBotManagerContext,
  eventData,
  onSuccess,
  onError = () => {},
  onInvalidCredential,
}: UseEmailPasswordLoginOptions): UseEmailPasswordLogin => {
  const [needsTwoFactorAuth, setNeedsTwoFactorAuth] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<ReactNode>();
  const { trackButton } = useTrackClickButton();

  const {
    isRetrievingTokenRef,
    getTokenData: getBotManagerTokenData,
    reset: resetBotManager,
  } = useBotManager({
    arkoseBotManager: {
      context: arkoseBotManagerContext,
      publicKey: ARKOSE_LOGIN_KEY,
    },
    reCAPTCHA: {
      container: LOGIN_REDESIGN_RECAPTCHA_ID,
      options: { action: RECAPTCHA_ACTIONS.login },
    },
  });

  /* tracks click events to submit button regardless of successful or not */
  const trackLoginClick = (status: ClickStatus) => {
    trackButton({
      name: 'login_with_email',
      clickStatus: status,
    });
  };

  const handleError = (response: unknown, overrideMessages?: Partial<Record<string, ReactNode>>) => {
    resetBotManager();

    const code = getErrorCode(response);

    const overrideMessage = code ? overrideMessages?.[code] : undefined;
    if (overrideMessage) {
      setError(overrideMessage);
      setIsRunning(false);
      return;
    }

    if (!code || !HANDLED_ERRORS.includes(code)) {
      onError(response);
      return;
    }

    setError(getHandledErrorMessage(code));
    setNeedsTwoFactorAuth(NEEDS_2FA_ERRORS.includes(code));
    setIsRunning(false);
    if (isInvalidCredentialCode(code)) {
      onInvalidCredential?.();
    }
  };

  const login = async (values: LoginValues) => {
    // Exit early if a token retrieval is in progress to prevent multiple function calls.
    if (isRetrievingTokenRef?.current) return false;

    if (!values.email || !values.password) {
      trackLoginClick('invalid');
      return setError(errorMessageFactory().invalidRequest);
    }

    let tokenData;
    try {
      tokenData = await getBotManagerTokenData();
    } catch {
      trackLoginClick('invalid');
      return setError(generalErrorMessage());
    }

    setIsRunning(true);

    const payload = {
      ...omit(values, 'one-time-password'),
      ...tokenData,
      ...(values['one-time-password'] && { mfa: values['one-time-password'] }),
    };

    trackAuthAttempts(eventData, 'login');

    try {
      const response = await actions.login(payload);
      trackLoginClick('valid');

      return onSuccess(response?.redirectTo as string);
    } catch (loginError) {
      // The case where 2FA is required is treated as an error in the FE but shouldn't be for the sake of tracking
      if (!(getErrorCode(loginError) === 'twoFactorRequired')) {
        trackLoginClick('invalid');
      }
      return handleError(loginError);
    }
  };

  return {
    login,
    error,
    isRunning,
    needsTwoFactorAuth,
    setError,
    setIsRunning,
    trackLoginClick,
    handleError,
  };
};

export default useEmailPasswordLogin;
